import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";

import { useEnable2FA } from "../hooks/api/use-enable-2fa";
import { useGenerate2FASecret } from "../hooks/api/use-generate-2fa-secret";

import { TwoFactorCodeInput } from "./2fa-code-input";
import { TwoFactorQRCodeDisplay } from "./2fa-qr-code-display";
import { BackupCodesDisplay } from "./backup-codes-display";

interface Forced2FASetupPageProps {
  userEmail: string;
  onComplete: () => void;
}

type Step = "generate" | "verify" | "backup-codes";

export function Forced2FASetupPage({ userEmail, onComplete }: Forced2FASetupPageProps) {
  const { t } = useTranslation("two-factor-auth");
  const { t: tCommon } = useTranslation("common");
  const [step, setStep] = useState<Step>("generate");
  const [secret, setSecret] = useState<string | null>(null);
  const [otpAuthUrl, setOtpAuthUrl] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // useGenerate2FASecret / useEnable2FA internally pick the bearer (full access
  // token vs short-lived 2fa_setup token) based on which is present in storage,
  // so this component is the same code for both populations.
  const { generateSecret, loading: generatingSecret } = useGenerate2FASecret({
    onCompleted: (data) => {
      setSecret(data.secret);
      setOtpAuthUrl(data.otpAuthUrl);
      setStep("verify");
    },
    onError: () => setError(t("errors.generic")),
  });

  const { enable2FA, loading: enabling } = useEnable2FA({
    onCompleted: (data) => {
      setBackupCodes(data.backupCodes);
      setStep("backup-codes");
    },
    onError: () => setError(t("errors.invalidCode")),
  });

  const handleGenerateSecret = () => {
    setError(null);
    generateSecret();
  };

  const handleVerify = () => {
    if (verificationCode.length !== 6) {
      setError(t("codeInput.invalidLength"));
      return;
    }

    setError(null);
    enable2FA({ code: verificationCode });
  };

  const handleContinue = () => {
    onComplete();
  };

  if (step === "generate") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-6 rounded-md border bg-card p-6 shadow-lg">
          <div className="text-center">
            <h1 className="mb-2 text-2xl font-semibold">{t("forced.title")}</h1>
            <p className="text-sm text-muted-foreground">
              {t("forced.description")}
            </p>
          </div>

          {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}

          <div>
            <Button type="button" onClick={handleGenerateSecret} disabled={generatingSecret} className="w-full">
              {generatingSecret ? <LoadingDots color="#fff" /> : tCommon("actions.next")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "verify") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-6 rounded-md border bg-card p-6 shadow-lg">
          <div className="text-center">
            <h1 className="mb-2 text-2xl font-semibold">{t("forced.setupTitle")}</h1>
          </div>

          {secret && otpAuthUrl && (
            <TwoFactorQRCodeDisplay otpAuthUrl={otpAuthUrl} secret={secret} accountName={userEmail} />
          )}

          <TwoFactorCodeInput value={verificationCode} onChange={setVerificationCode} error={error || undefined} />

          <div>
            <Button
              type="button"
              onClick={handleVerify}
              disabled={enabling || verificationCode.length !== 6}
              className="w-full"
            >
              {enabling ? <LoadingDots color="#fff" /> : t("enable.confirmAndActivate")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "backup-codes") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-6 rounded-md border bg-card p-6 shadow-lg">
          <BackupCodesDisplay backupCodes={backupCodes} onContinue={handleContinue} />
        </div>
      </div>
    );
  }

  return null;
}
